type AdminEdmErrorOperation =
  | "load-datasets"
  | "create-dataset"
  | "load-dataset"
  | "save-dataset"
  | "recompute-dataset"
  | "load-dataset-summary"
  | "load-dataset-blocked"
  | "validate-dataset-media"
  | "generate-dataset-handoff"
  | "load-policy"
  | "load-artworks"
  | "load-artwork"
  | "save-artwork-profile"
  | "recompute-artwork"
  | "transition-artwork"
  | "validate-artwork-media";

type AdminEdmApiErrorCode =
  | "VALIDATION_ERROR"
  | "UNAUTHORIZED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "CONFLICT"
  | "RATE_LIMITED"
  | "INTERNAL";

type AdminEdmApiErrorLike = {
  status?: boolean;
  error?: {
    code?: string | null;
    message?: string | null;
    details?: unknown;
    statusCode?: number | null;
  };
};

export type AdminEdmUiError = {
  message: string;
  details?: unknown;
};

export const ARTWORK_NOT_FOUND_MESSAGE = "Artwork not found.";
export const DATASET_NOT_FOUND_MESSAGE = "Dataset not found.";

const SESSION_EXPIRED_MESSAGE = "Your session expired. Please sign in again and try again.";
const RATE_LIMITED_MESSAGE = "Too many requests. Please wait a moment and try again.";
const STALE_RECORD_MESSAGE = "This item changed on the server. Refresh and try again.";

const DEFAULT_MESSAGES: Record<AdminEdmErrorOperation, string> = {
  "load-datasets": "Could not load EDM datasets.",
  "create-dataset": "Could not create this dataset.",
  "load-dataset": "Could not load this dataset.",
  "save-dataset": "Could not save this dataset.",
  "recompute-dataset": "Could not recompute readiness for this dataset.",
  "load-dataset-summary": "Could not load this dataset summary.",
  "load-dataset-blocked": "Could not load blocked records for this dataset.",
  "validate-dataset-media": "Could not validate public links for this dataset.",
  "generate-dataset-handoff": "Could not generate the handoff package for this dataset.",
  "load-policy": "Could not load EDM policy.",
  "load-artworks": "Could not load EDM artworks.",
  "load-artwork": "Could not load this artwork.",
  "save-artwork-profile": "Could not save this artwork.",
  "recompute-artwork": "Could not recompute readiness for this artwork.",
  "transition-artwork": "Could not change the publication state for this artwork.",
  "validate-artwork-media": "Could not validate public links for this artwork.",
};

const VALIDATION_MESSAGES: Partial<Record<AdminEdmErrorOperation, string>> = {
  "create-dataset": "Could not create this dataset. Review the dataset fields and try again.",
  "save-dataset": "Could not save this dataset. Review the dataset fields and try again.",
  "save-artwork-profile": "Could not save this artwork. Review the highlighted EDM fields and try again.",
};

const FORBIDDEN_MESSAGES: Partial<Record<AdminEdmErrorOperation, string>> = {
  "load-datasets": "You do not have permission to view EDM datasets.",
  "create-dataset": "You do not have permission to create EDM datasets.",
  "load-dataset": "You do not have permission to view this dataset.",
  "save-dataset": "You do not have permission to edit this dataset.",
  "recompute-dataset": "You do not have permission to recompute readiness for this dataset.",
  "load-dataset-summary": "You do not have permission to view this dataset summary.",
  "load-dataset-blocked": "You do not have permission to view blocked records for this dataset.",
  "validate-dataset-media": "You do not have permission to validate public links for this dataset.",
  "generate-dataset-handoff": "You do not have permission to generate a handoff package for this dataset.",
  "load-policy": "You do not have permission to view EDM policy.",
  "load-artworks": "You do not have permission to view EDM artworks.",
  "load-artwork": "You do not have permission to view this artwork.",
  "save-artwork-profile": "You do not have permission to edit this artwork.",
  "recompute-artwork": "You do not have permission to recompute readiness for this artwork.",
  "transition-artwork": "You do not have permission to change the publication state for this artwork.",
  "validate-artwork-media": "You do not have permission to validate public links for this artwork.",
};

const NOT_FOUND_MESSAGES: Partial<Record<AdminEdmErrorOperation, string>> = {
  "load-dataset": DATASET_NOT_FOUND_MESSAGE,
  "load-artwork": ARTWORK_NOT_FOUND_MESSAGE,
};

const normalizeErrorCode = (error?: AdminEdmApiErrorLike["error"]): AdminEdmApiErrorCode => {
  const code = typeof error?.code === "string" ? error.code.trim() : "";
  if (code === "VALIDATION_ERROR" || code === "UNAUTHORIZED" || code === "FORBIDDEN" || code === "NOT_FOUND" || code === "CONFLICT" || code === "RATE_LIMITED") {
    return code;
  }

  switch (error?.statusCode) {
    case 400:
      return "VALIDATION_ERROR";
    case 401:
      return "UNAUTHORIZED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 409:
      return "CONFLICT";
    case 429:
      return "RATE_LIMITED";
    default:
      return "INTERNAL";
  }
};

const hasContextValidationDetails = (details: unknown): boolean => {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return false;
  }

  const contextEntities = (details as { contextEntities?: unknown }).contextEntities;
  return Array.isArray(contextEntities) && contextEntities.length > 0;
};

const hasParadataValidationDetails = (details: unknown): boolean => {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return false;
  }

  const paradata = (details as { paradata?: unknown }).paradata;
  return Array.isArray(paradata) && paradata.length > 0;
};

const getFriendlyMessage = (
  operation: AdminEdmErrorOperation,
  code: AdminEdmApiErrorCode,
  details: unknown
): string => {
  switch (code) {
    case "UNAUTHORIZED":
      return SESSION_EXPIRED_MESSAGE;
    case "RATE_LIMITED":
      return RATE_LIMITED_MESSAGE;
    case "CONFLICT":
      return STALE_RECORD_MESSAGE;
    case "FORBIDDEN":
      return FORBIDDEN_MESSAGES[operation] || DEFAULT_MESSAGES[operation];
    case "NOT_FOUND":
      return NOT_FOUND_MESSAGES[operation] || DEFAULT_MESSAGES[operation];
    case "VALIDATION_ERROR":
      if (operation === "save-artwork-profile" && hasParadataValidationDetails(details)) {
        return "Could not save this artwork. Review the highlighted paradata fields and try again.";
      }
      if (operation === "save-artwork-profile" && hasContextValidationDetails(details)) {
        return "Could not save this artwork. Review the highlighted contextual entities and try again.";
      }
      return VALIDATION_MESSAGES[operation] || DEFAULT_MESSAGES[operation];
    default:
      return DEFAULT_MESSAGES[operation];
  }
};

export const formatAdminEdmUiError = (
  operation: AdminEdmErrorOperation,
  data: AdminEdmApiErrorLike | null | undefined
): AdminEdmUiError => {
  const error = data?.error;
  const code = normalizeErrorCode(error);
  const details = error?.details;

  return {
    message: getFriendlyMessage(operation, code, details),
    details,
  };
};

export const isArtworkNotFoundMessage = (message?: string | null): boolean =>
  message === ARTWORK_NOT_FOUND_MESSAGE;

export const isDatasetNotFoundMessage = (message?: string | null): boolean =>
  message === DATASET_NOT_FOUND_MESSAGE;
